import { doc, getDoc, serverTimestamp, setDoc, Timestamp } from "firebase/firestore";
import { db, auth } from "@/firebase/config";
import type { BonusQuestionsRepository } from "@/modules/leagues/repositories/BonusQuestionsRepository";
import type { BonusAnswer, BonusQuestionsConfig } from "@/modules/leagues/types/bonusQuestions";
import { BONUS_QUESTION_ORDER } from "@/modules/leagues/types/bonusQuestions";

/**
 * `groups/{leagueId}/bonusQuestions/config` — doc único com a config da
 * liga (quais perguntas ativas, pontos, prazo). `groups/{leagueId}/bonusAnswers/{uid}`
 * — um doc por participante com as respostas dele. Ver firestore.rules:
 * config só é editável por admin/moderador; respostas só pelo próprio uid
 * e só antes do prazo (bloqueio real, não só visual).
 */
export class FirestoreBonusQuestionsRepository implements BonusQuestionsRepository {
  private requireUid(): string {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Você precisa estar autenticado para essa ação.");
    return uid;
  }

  private configRef(leagueId: string) {
    return doc(db, "groups", leagueId, "bonusQuestions", "config");
  }

  private answerRef(leagueId: string, uid: string) {
    return doc(db, "groups", leagueId, "bonusAnswers", uid);
  }

  async getConfig(leagueId: string): Promise<BonusQuestionsConfig | null> {
    const snap = await getDoc(this.configRef(leagueId));
    if (!snap.exists()) return null;
    const data = snap.data();
    const deadline = data.deadline instanceof Timestamp ? data.deadline.toDate().toISOString() : (data.deadline ?? null);
    const scoredAt = data.scoredAt instanceof Timestamp ? data.scoredAt.toDate().toISOString() : (data.scoredAt ?? null);
    return {
      deadline,
      questions: data.questions ?? {},
      scored: data.scored ?? false,
      scoredAt,
      finalResult: data.finalResult ?? null,
    } as BonusQuestionsConfig;
  }

  async saveConfig(leagueId: string, config: Pick<BonusQuestionsConfig, "deadline" | "questions">): Promise<void> {
    // Sempre grava as 4 chaves fixas (mesmo as inativas) — evita doc
    // parcial que confundiria uma leitura futura sobre o que "não foi
    // configurado ainda" vs. "foi desativado de propósito".
    const questions = Object.fromEntries(
      BONUS_QUESTION_ORDER.map((type) => [type, config.questions[type] ?? { active: false, pontos: 0 }])
    );
    await setDoc(
      this.configRef(leagueId),
      {
        deadline: config.deadline ? Timestamp.fromDate(new Date(config.deadline)) : null,
        questions,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  async getMyAnswer(leagueId: string): Promise<BonusAnswer | null> {
    const uid = this.requireUid();
    const snap = await getDoc(this.answerRef(leagueId, uid));
    if (!snap.exists()) return null;
    const data = snap.data();
    const updatedAt = data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : new Date().toISOString();
    return { uid, answers: data.answers ?? {}, updatedAt };
  }

  async saveMyAnswer(leagueId: string, answers: BonusAnswer["answers"]): Promise<void> {
    const uid = this.requireUid();
    await setDoc(
      this.answerRef(leagueId, uid),
      { uid, answers, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }
}
